import React, { useId, useState } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';

export const EMAIL_LOCKED_TOOLTIP =
  'Email notifications coming soon. Available with Aura Pro.';

/**
 * Disabled email channel control — preserves saved enabled state visually
 * without allowing toggles or API updates.
 */
export const LockedEmailCheckbox = ({
  id: idProp,
  label = 'Email',
  checked = false,
  className = '',
}) => {
  const autoId = useId();
  const id = idProp || `locked-email-${autoId}`;
  const [tipVisible, setTipVisible] = useState(false);

  const showTip = () => setTipVisible(true);
  const hideTip = () => setTipVisible(false);

  return (
    <div
      className={`relative opacity-50 ${className}`}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onFocus={showTip}
      onBlur={hideTip}
    >
      <label htmlFor={id} className="flex items-center gap-3 cursor-not-allowed py-2">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled
          readOnly
          aria-disabled="true"
          className="checkbox-editorial pointer-events-none"
        />
        <span className="text-body text-ink-muted flex items-center gap-1.5">
          {label}
          <LockClosedIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
        </span>
      </label>
      {tipVisible && (
        <div
          role="tooltip"
          className="absolute left-0 top-full z-20 mt-1 max-w-xs rounded-md border border-hairline bg-bone-raised p-2 text-label text-secondary"
        >
          {EMAIL_LOCKED_TOOLTIP}
        </div>
      )}
    </div>
  );
};

/**
 * Disabled email toggle row (matches EditorialToggle layout).
 */
export const LockedEmailToggle = ({
  id: idProp,
  label = 'Email',
  checked = false,
  description,
}) => {
  const autoId = useId();
  const id = idProp || `locked-email-toggle-${autoId}`;
  const [tipVisible, setTipVisible] = useState(false);

  return (
    <div
      className="relative flex flex-wrap items-center justify-between gap-4 py-4 opacity-50"
      onMouseEnter={() => setTipVisible(true)}
      onMouseLeave={() => setTipVisible(false)}
    >
      <div className="min-w-0 flex-1">
        <p className="text-body text-ink-muted flex items-center gap-1.5" id={`${id}-label`}>
          {label}
          <LockClosedIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
        </p>
        {description && <p className="text-label text-secondary mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-disabled="true"
        disabled
        className={`toggle-editorial pointer-events-none ${checked ? 'toggle-editorial-on' : ''}`}
        tabIndex={-1}
      >
        <span className="toggle-editorial-knob" aria-hidden />
      </button>
      {tipVisible && (
        <div
          role="tooltip"
          className="absolute left-0 top-full z-20 mt-1 max-w-xs rounded-md border border-hairline bg-bone-raised p-2 text-label text-secondary"
        >
          {EMAIL_LOCKED_TOOLTIP}
        </div>
      )}
    </div>
  );
};

/**
 * Wraps email notification settings blocks (newsletter, snapshot, etc.).
 */
export const LockedEmailSection = ({ title, description, children }) => {
  const [tipVisible, setTipVisible] = useState(false);

  return (
    <div
      className="relative opacity-50"
      onMouseEnter={() => setTipVisible(true)}
      onMouseLeave={() => setTipVisible(false)}
    >
      <div className="flex items-start gap-2 mb-3">
        <LockClosedIcon className="w-5 h-5 shrink-0 text-ink-muted mt-0.5" aria-hidden="true" />
        <div>
          {title && <p className="text-body text-ink-muted font-medium">{title}</p>}
          {description && <p className="text-label text-secondary mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="pointer-events-none select-none">{children}</div>
      {tipVisible && (
        <div
          role="tooltip"
          className="absolute left-0 top-full z-20 mt-1 max-w-sm rounded-md border border-hairline bg-bone-raised p-2 text-label text-secondary"
        >
          {EMAIL_LOCKED_TOOLTIP}
        </div>
      )}
    </div>
  );
};
